'use client';

import { useEffect, useRef, useState } from 'react';

interface LazyVideoProps {
  poster: string;
  sources: Array<{
    src: string;
    type: string;
    media?: string;
  }>;
  className?: string;
  ariaLabel?: string;
}

export default function LazyVideo({ poster, sources, className, ariaLabel }: LazyVideoProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Use IntersectionObserver to only load video when in viewport
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isLoaded) {
            // Load video sources
            const sourceElements = video.querySelectorAll('source');
            sourceElements.forEach((source) => {
              const dataSrc = source.getAttribute('data-src');
              if (dataSrc) {
                source.setAttribute('src', dataSrc);
              }
            });
            
            // Load and play video
            video.load();
            video.play().catch(() => {
              // Auto-play might be blocked, that's okay
            });
            
            setIsLoaded(true);
            observer.disconnect();
          }
        });
      },
      {
        rootMargin: '200px', // Start loading 200px before video enters viewport
        threshold: 0.1,
      }
    );

    observer.observe(video);

    return () => observer.disconnect();
  }, [isLoaded]);

  return (
    <video
      ref={videoRef}
      className={className}
      loop
      muted
      playsInline
      poster={poster}
      aria-label={ariaLabel}
      preload="none"
    >
      {sources.map((source, index) => (
        <source
          key={index}
          data-src={source.src}
          type={source.type}
          media={source.media}
        />
      ))}
      {/* Empty captions track for accessibility compliance */}
      <track kind="captions" />
      Your browser does not support the video tag.
    </video>
  );
}

